import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Tooltip } from 'antd';
import type { DraftPlan, PlanSession } from '../../features/aiPlanner/plannerApi';
import ChatPanel from './ChatPanel';
import TaskCards, { type CoordinatorOption } from './TaskCards';
import { type WsUI, SIZE_LABEL, annotate, countItems, prettyDate, reannotate, strip } from './planUi';

interface Props {
  session: PlanSession;
  coordinators: CoordinatorOption[];
  engagementDate?: string | null;
  onSend: (text: string, draft: DraftPlan) => Promise<unknown>;
  onRevert: (version: number) => Promise<unknown>;
  onApply: (finalDraft: DraftPlan, updateEngagementDate: boolean) => void;
  onCancel: () => void;
  sending: boolean;
  applying: boolean;
}

const SUGGESTIONS = [
  'Fewer tasks — keep only what really matters',
  'Spread the work across more people',
  'Add a rehearsal two days before',
  'Move every deadline three days earlier',
];

/** Step 2 (plan mode) — the plan on the left, the conversation on the right. */
export default function PlanStep({
  session, coordinators, engagementDate, onSend, onRevert, onApply, onCancel, sending, applying,
}: Props) {
  const plan = session.draft_plan ?? {};
  const [ws, setWs] = useState<WsUI[]>(() => annotate(plan.workstreams));
  const version = session.revisions?.[session.revisions.length - 1]?.version ?? 0;

  // a new version arrived (chat turn / undo) -> reload the cards, keep the team's edit flags
  useEffect(() => {
    setWs((prev) => reannotate(session.draft_plan?.workstreams, prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  const recordDate = engagementDate ? engagementDate.slice(0, 10) : null;
  const eventDate = plan.event_date || recordDate;
  const dateChanged = !!plan.event_date && plan.event_date !== recordDate;
  const [updateDate, setUpdateDate] = useState(true);

  const total = useMemo(() => countItems(ws), [ws]);
  const lastAi = [...(session.messages ?? [])].reverse().find((m) => m.role === 'assistant');
  const highlight = useMemo(() => new Set(lastAi?.changed_refs ?? []), [lastAi]);

  const draft = (): DraftPlan => ({ ...plan, workstreams: strip(ws) });

  return (
    <div className="flex flex-col gap-3">
      {/* summary strip */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-line-soft bg-white px-4 py-2.5 text-sm">
        <div className="flex flex-col">
          <span className="text-[11px] font-medium uppercase tracking-wide text-[#9ca3af]">Plan</span>
          <span className="text-ink"><span className="font-semibold">{total}</span> tasks and sub-tasks · {ws.length} parts</span>
        </div>
        {plan.complexity && (
          <div className="flex flex-col">
            <span className="text-[11px] font-medium uppercase tracking-wide text-[#9ca3af]">Size</span>
            <Tooltip title={plan.complexity_note}>
              <span className="cursor-default text-ink underline decoration-dotted decoration-line underline-offset-2">
                {SIZE_LABEL[plan.complexity] ?? plan.complexity} event
              </span>
            </Tooltip>
          </div>
        )}
        {eventDate && (
          <div className="flex flex-col">
            <span className="text-[11px] font-medium uppercase tracking-wide text-[#9ca3af]">Event date</span>
            <span className="text-ink">{prettyDate(eventDate)}</span>
          </div>
        )}
        <div className="ml-auto flex flex-col text-right">
          <span className="text-[11px] font-medium uppercase tracking-wide text-[#9ca3af]">Version</span>
          <span className="text-ink">{version || 1}</span>
        </div>
      </div>

      {dateChanged && (
        <Alert type="warning" showIcon
          message={`Your description gives a new date: ${prettyDate(plan.event_date!)}`}
          description={
            <div className="flex flex-col gap-1">
              <span>{recordDate ? `The engagement record still says ${prettyDate(recordDate)}. ` : ''}All "do it by" dates are counted from the new date.</span>
              <Checkbox checked={updateDate} onChange={(e) => setUpdateDate(e.target.checked)}>
                Also update the engagement's date to {prettyDate(plan.event_date!)} when creating the tasks
              </Checkbox>
            </div>
          } />
      )}

      {/* two panes */}
      <div className="flex h-[calc(100vh-270px)] min-h-[480px] flex-col overflow-hidden rounded-lg border border-line-soft lg:flex-row">
        <div className="min-h-0 flex-[5] overflow-y-auto bg-bg/40 px-5 py-3">
          <TaskCards value={ws} onChange={setWs} coordinators={coordinators}
            eventDate={eventDate} highlightRefs={highlight} allowNewPart />
        </div>
        <div className="min-h-[260px] flex-[3] border-t border-line-soft lg:min-w-[360px] lg:max-w-[460px] lg:border-l lg:border-t-0">
          <ChatPanel
            messages={session.messages ?? []} revisions={session.revisions ?? []}
            suggestions={SUGGESTIONS} sending={sending}
            onSend={(t) => onSend(t, draft())} onRevert={onRevert}
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 pt-1">
        <span className="text-xs text-ink-secondary">Nothing is saved to the engagement until you create the tasks.</span>
        <div className="flex gap-2">
          <Button onClick={onCancel} disabled={applying}>Cancel</Button>
          <Button type="primary" loading={applying} disabled={total === 0 || sending}
            onClick={() => onApply(draft(), dateChanged && updateDate)}>
            Create {total} task(s)
          </Button>
        </div>
      </div>
    </div>
  );
}
